<template>
  <component :is="componentTag" :class="classes" :tag="tagAttr" :to="to" :exact="exact" @click="onClick">
    <div v-if="$slots.icon" class="button__icon-wrapper">
      <slot name="icon" />
    </div>

    <slot />

    <circular-spinner
      v-if="loading"
      class="button__circular-spinner"
      :inverse="secondary || ghost || tertiary ? inverted : !inverted"
      :dark="secondary || ghost || tertiary ? !inverted : inverted"
    />
  </component>
</template>

<script setup lang="ts">
import CircularSpinner from '@components/circular-spinner.vue';
import { type RouteLocationRaw } from 'vue-router';
import FsRouterLink from '@components/fs-router-link.vue';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    secondary?: boolean;
    tertiary?: boolean;
    ghost?: boolean;
    /** Accent (yellow) fill — the app's primary call-to-action style */
    accent?: boolean;
    /** Button size */
    size?: 'pico' | 'nano' | 'micro' | 'tiny' | 'extra-small' | 'small' | 'medium' | 'large' | 'huge';
    inverted?: boolean;
    /** Show button in disabled state */
    disabled?: boolean;
    /** When loading */
    loading?: boolean;
    /** The tag to use as the button element (can for example be an anchor)
        Defaults to button, unless component is treated as a router-link wrapper, in which it defaults to the anchor tag */
    tag?: string;
    /** Special properties used when using component as a router-link wrapper
        Pass anything to the to attribute and the component will be treated as a router-link wrapper */
    to?: RouteLocationRaw;
    exact?: boolean;
    /** it's needed for tertiary button. It should be optional for backward compatibility */
    noSidePaddings?: boolean;
  }>(),
  {
    secondary: false,
    tertiary: false,
    ghost: false,
    size: 'medium',
    inverted: false,
    disabled: false,
    loading: false,
  }
);

const emits = defineEmits<{
  click: [event: MouseEvent];
}>();

const buttonType = computed(() => {
  if (props.secondary) return 'secondary';
  if (props.ghost) return 'ghost';
  if (props.tertiary) return 'tertiary';
  return null;
});

const classes = computed(() => {
  const classes = ['button'];

  if (props.disabled) classes.push(`button--disabled`);
  if (props.inverted) classes.push(`button--inverted`);
  if (props.loading) classes.push(`button--loading`);
  if (props.size) classes.push(`button--${props.size}`);
  if (buttonType.value) classes.push(`button--${buttonType.value}`);
  if (props.accent) classes.push(`button--accent`);
  if (props.noSidePaddings) classes.push(`button--no-side-paddings`);

  return classes;
});

const componentTag = computed(() => (props.to ? FsRouterLink : props.tag || 'button'));
const tagAttr = computed(() => props.to && (props.tag || 'a'));

function onClick(event: MouseEvent) {
  if (props.loading) return;

  emits('click', event);
}
</script>

<style scoped lang="scss">
@import '@styles/variables';
@import '@styles/text';

// Inspired by bootstrap - https://github.com/twbs/bootstrap/blob/master/scss/_buttons.scss
.button {
  @include button;
  position: relative;
  font-weight: 400;
  background-color: $c-accent;
  border: 1px solid transparent;
  border-radius: $br-round-corner;
  color: $c-on-accent;
  fill: $c-on-accent;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  /*vertical-align: top;*/ // Removed because it makes it less inline'y. Originally removed due to link--button (see link.scss)
  padding: 1rem 2.4rem;
  user-select: none;
  cursor: pointer;
  transition:
    color 0.15s ease-in-out,
    background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out,
    box-shadow $transition-smooth $ease-out-soft,
    transform $transition-fast $ease-spring,
    opacity 0.1s ease;
  white-space: nowrap;
  background-clip: border-box;
  outline: none;

  &:hover {
    background-color: $c-accent-deep;
    opacity: 1;
    transform: translateY(-1px);
    box-shadow: $shadow-pill;
  }

  &:active {
    background-color: $c-accent-deep;
    opacity: 1;
    transform: translateY(0) scale(0.98);
    box-shadow: none;
  }

  // Secondary button style
  &--secondary {
    border-color: $c-dark;
    color: $c-dark;
    fill: $c-dark;
    background: transparent !important;

    &.button--inverted {
      border-color: $c-light;
      color: $c-light;
      fill: $c-light;
    }
  }

  // Ghost button style
  &--ghost {
    border-color: transparent;
    color: $c-dark;
    fill: $c-dark;
    background: transparent !important;

    &:hover,
    &:active {
      border-color: $c-dark;
      opacity: 1;
    }

    &.button--inverted {
      color: $c-light;
      fill: $c-light;

      &:hover,
      &:active {
        border-color: $c-light;
        opacity: 1;
      }
    }
  }

  // Tertiary button style
  &--tertiary {
    border-color: transparent;
    color: $c-dark;
    fill: $c-dark;
    background: transparent !important;

    &.button--inverted {
      color: $c-light;
      fill: $c-light;
    }
  }

  // Accent (yellow) fill — primary call-to-action
  &--accent {
    background-color: $c-accent;
    color: $c-on-accent;
    fill: $c-on-accent;

    &:hover {
      background-color: $c-accent-deep;
      opacity: 1;
    }

    &:active {
      background-color: $c-accent-deep;
    }
  }

  // Disabled styling
  &--disabled {
    opacity: 0.6 !important;
    pointer-events: none !important;
  }

  // Inverted, when on dark background
  &--inverted {
    background-color: $c-bg-light;
    color: $c-dark;
    fill: $c-dark;
  }

  // Sizing
  &--pico {
    height: 1.8rem;
    padding: 0 1rem;
    font-size: 1rem;
    font-weight: 400;
    text-transform: none;
  }
  &--nano {
    height: 1.5rem;
    font-size: 1rem;
    font-weight: 400;
    text-transform: none;
  }
  &--micro {
    height: 2rem;
    font-size: 1.2rem;
    font-weight: 400;
    text-transform: none;
  }
  &--tiny {
    height: 2.4rem;
    max-height: 2.4rem;
    padding: 1px 1.4rem 0;
    font-size: 1.2rem;
    font-weight: 400;
    text-transform: none;
  }
  &--extra-small {
    @include button--small;
    min-height: 2.9rem;
    max-height: 2.9rem;
  }
  &--small {
    @include button--small;
    max-height: 3.5rem;
  }

  // Default CTA size — now sized to match the `small` variant (3.1rem) so the
  // default button lines up with the default fs-input. Existing call sites
  // that don't pass a `size` prop pick up this change for free.
  &--medium {
    @include button--small;
    font-size: 1.3rem;
    min-height: 3.1rem;
    max-height: 3.1rem;
    padding: 0.4rem 1.6rem;

    :deep(.icon) {
      position: relative;
    }
  }

  &--large {
    @include button--large;
  }

  &--huge {
    @include button--huge;
  }

  // Loading styling
  // Hide slot content when loading
  &--loading {
    > * {
      visibility: hidden;
    }
    > .button__circular-spinner {
      visibility: visible;
    }
    color: rgba(0, 0, 0, 0) !important;
  }

  &--no-side-paddings {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  &__circular-spinner {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
  }

  &__icon-wrapper {
    height: 100%;
    width: 15px;
    margin-right: 5px;
    padding: 0 0 1px 1px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    position: relative;
    overflow: hidden;
  }
}
</style>
